import { Warning } from "@phosphor-icons/react"
import React, { useState } from "react"
import { Button } from "~/components/reusable/Button"
import { DisabledTooltip } from "~/components/reusable/DisabledTooltip"
import { useErpLockedFields } from "~/hooks/useErpLockedFields"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { RadioGroup, RadioGroupItem } from "~/components/reusable/RadioGroup"
import { useAddVariantsToCollection } from "~/hooks/useCollections"
import { useGetNoosVariants, useMakeVariantsNoos } from "~/hooks/useSku"
import { getTailwindValue } from "~/utils/tailwind"

enum HandleConflictOptions {
  Skip = "skip",
  Convert = "convert",
}

type Props = {
  collectionId: number
  variantIds: string[]
  setOpen: (value: boolean) => void
}

export default function HandleAddVariantConflictContent({
  variantIds,
  collectionId,
  setOpen,
}: Props) {
  const [selectedChoice, setSelectedChoice] = useState<HandleConflictOptions>()

  const { mutate: addVariants, isPending: isAddingVariants } = useAddVariantsToCollection()
  const { mutate: makeVariantsNoos, isPending: isMakingVariantsNoos } = useMakeVariantsNoos()

  // Converting writes productType.
  const { isFieldLocked, lockReason } = useErpLockedFields()
  const isProductTypeLocked = isFieldLocked("productType")
  const { data: noosVariants, isPending: gettingNoosVariants } = useGetNoosVariants(variantIds)

  const onSubmitHandling = () => {
    if (selectedChoice === HandleConflictOptions.Convert) {
      makeVariantsNoos(
        { variantIds },
        {
          onSuccess: () => {
            addVariants(
              {
                collectionId: collectionId,
                variantIds,
              },
              {
                onSuccess: () => {
                  setOpen(false)
                },
              },
            )
          },
        },
      )
    } else {
      addVariants(
        {
          collectionId: collectionId,
          variantIds: noosVariants ?? [],
        },
        {
          onSuccess: () => {
            setOpen(false)
          },
        },
      )
    }
  }

  return (
    <DialogContent className="w-full min-w-[640px]">
      <DialogHeader className="">
        <DialogTitle className="pt-[2px]">Incompatible Product Type</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-5">
        <div className="flex rounded-lg border-[1px] border-red-300 bg-red-100 py-[10px] px-3 text-nowrap items-center gap-2 paragraph-base">
          <Warning
            size={16}
            weight="bold"
            format="outline"
            color={getTailwindValue("--color-red-600")}
          />
          You’re trying to add Fashion products to a NOOS collection.
        </div>
        <div className="flex flex-col gap-3">
          <div className="label-base text-fg-subtle">Choose how you want to proceed</div>
          <RadioGroup
            value={selectedChoice}
            onValueChange={(e) => setSelectedChoice(e as HandleConflictOptions)}
          >
            <div className="flex flex-col gap-4">
              <DisabledTooltip title={lockReason("productType")} wrapperClassName="flex gap-3">
                <div
                  className="flex gap-3"
                  onClick={() =>
                    !isProductTypeLocked && setSelectedChoice(HandleConflictOptions.Convert)
                  }
                >
                  <div className="flex flex-col items-start justify-start w-5 h-5 p-[2px]">
                    <RadioGroupItem
                      value={HandleConflictOptions.Convert}
                      disabled={isProductTypeLocked}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="label-base"> Convert selected product(s) to NOOS </div>
                    <div className="paragraph-base text-fg-muted text-nowrap">
                      Fashion products will be converted to NOOS before adding to the collection.
                    </div>
                  </div>
                </div>
              </DisabledTooltip>

              <div
                className="flex gap-3"
                onClick={() => setSelectedChoice(HandleConflictOptions.Skip)}
              >
                <div className="flex flex-col items-start justify-start w-5 h-5 p-[2px]">
                  <RadioGroupItem value={HandleConflictOptions.Skip} />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="label-base">Skip all Fashion products</div>
                  <div className="paragraph-base text-fg-muted">
                    Fashion products will not be added to this collection. This will result in{" "}
                    {`${noosVariants?.length ?? 0}/${variantIds.length}`} variants being added to
                    the collection.
                  </div>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>
      </div>
      <DialogFooter className="justify-between">
        <DialogClose>
          <Button asChild variant="transparent">
            Cancel
          </Button>
        </DialogClose>
        <Button
          requires="edit"
          onClick={onSubmitHandling}
          disabled={
            !selectedChoice || isAddingVariants || isMakingVariantsNoos || gettingNoosVariants
          }
        >
          Confirm
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
